import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useToast } from '~/lib/ui/toast'
import { cn } from '~/lib/utils'
import { Button } from '~/lib/ui/button'
import YnsIcon from '../icons/YnsIcon'

export const Dropzone = ({
  onFileSelected = () => null,
  className,
  title,
  children,
  preWork = false,
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const { toast } = useToast()

  const onDropAccepted = (incomingFiles) => {
    const incomingFile = incomingFiles[0]
    onFileSelected(incomingFile)
  }

  const onDropRejected = (incomingFiles) => {
    const {
      file: { type },
      errors,
    } = incomingFiles[0]
    const description = errors.map((e) => ` • ${e.message}`).join('\n')
    console.error('File upload rejected:', description)
    if (type) {
      toast({
        description,
        title: 'Could Not Upload Resume',
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: {
      '*/*/pdf': ['.pdf'],
      '*/*/msword': ['.doc'],
      '*/*/document': ['.docx'],
    },
    onDropAccepted,
    onDropRejected,
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: () => setIsDraggingOver(false),
    maxSize: 15_000_000, // 15MB
  })

  const baseRootClassName = cn(
    'min-h-[10rem] p-4 border-dashed border-2 border-input bg-input-background text-center content-center justify-items-center cursor-pointer rounded-lg shadow-sm',
    className,
  )
  const rootClassName = isDraggingOver
    ? baseRootClassName + ' border-primary border-2 bg-lily-blue'
    : baseRootClassName

  const label = preWork
    ? 'Upload the Pre-Work Here'
    : 'Upload the Revised Resume Here'

  return (
    <div className="pb-4">
      <div className="mb-2 flex flex-row items-center justify-start">
        {title ? (
          <div className="text-lg font-medium">{title}</div>
        ) : (
          <div className="text-lg font-medium">
            {label} <em>(required)</em>
          </div>
        )}
      </div>
      <div
        {...getRootProps({
          className: rootClassName,
        })}
      >
        <input {...getInputProps()} />
        <div>
          {children}
          <em>(Files must be in .pdf, .doc, or .docx format)</em>
        </div>
      </div>
    </div>
  )
}

export const AddMoreFiles = ({ setFiles }) => {
  const [dropzones, setDropzones] = useState([])
  const handleAddDropzone = () => {
    const lastVal = dropzones.length > 0 ? dropzones[dropzones.length - 1] : -1
    setDropzones((prev) => [...prev, lastVal + 1])
  }

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 gap-4">
        {dropzones.map((_, idx) => (
          <DropzoneItem
            key={`dropzone-${idx}`}
            setDropzones={setDropzones}
            setFiles={setFiles}
            index={idx}
            number={idx + 1}
          />
        ))}
      </div>
      <Button variant="link" onClick={handleAddDropzone} className="mt-2">
        + Add Additional Supporting Documents <em>(optional)</em>
      </Button>
    </div>
  )
}

export const DropzoneItem = ({ setFiles, index, number }) => {
  const handleRemoveDropzone = () => {
    setFiles((prevFiles) => prevFiles.filter((_, idx) => idx !== index))
  }
  const [otherFile, setOtherFile] = useState({})

  const onFileSelected = (file) => {
    setOtherFile(file)
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles]
      newFiles[index] = file
      return newFiles
    })
  }
  return (
    <div
      id={`dropzone-${index}`}
      className="dropzone-item flex items-center gap-2"
    >
      <Dropzone
        onFileSelected={onFileSelected}
        title={
          <>
            Upload Supporting Document {number} <em>(optional)</em>
          </>
        }
      >
        {otherFile.name ? (
          <>
            <p className="mb-4">
              <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
              {`${otherFile.name} (${(otherFile.size / 1024).toFixed(2)} KB) ${otherFile.type ? ` (${otherFile.type})` : ''}`}
            </p>
            <p>Drag and drop a new file to replace the current selection.</p>
          </>
        ) : (
          <p>
            {`Supporting Document ${number}`}
            <br />
            Drag and drop a supporting document here, or click to select
          </p>
        )}
      </Dropzone>
      <Button
        onClick={handleRemoveDropzone}
        variant="ghost"
        className="text-red-600 hover:text-red-400"
      >
        <YnsIcon name="trash" className="h-6 w-6" />
      </Button>
    </div>
  )
}
